/**
 * Builder presentation for lift pre/post halves.
 *
 * The tag reader itself is shared-types `readLiftTag`, the one TypeScript
 * owner the backend runtime also re-exports. Frontend-only helpers live here;
 * do not import Nest runtime.
 *
 * Labels match chat generation-preview: "Lift: pre-exposure" /
 * "Lift: post-exposure".
 */

import { findSurveyQuestion } from "@/features/studies/report/utils/findSurveyQuestion";

import {
  LIFT_POST_TAG_PREFIX,
  LIFT_PRE_TAG_PREFIX,
  LiftScorecard,
  LiftScorecardRow,
  LiftTagHalf,
  readLiftTag,
  readQuestionLiftHalf,
} from "@digital-twins/shared-types";

// One owner, shared-types `lift-tags.ts`.
// Re-exported for this module's existing readers.
export { LIFT_POST_TAG_PREFIX, LIFT_PRE_TAG_PREFIX, readLiftTag, readQuestionLiftHalf };
export type { LiftTagHalf };

export function liftHalfChipLabel(half: "pre" | "post"): string {
  return half === "pre" ? "Lift: pre-exposure" : "Lift: post-exposure";
}

export function liftHalfChipTooltip(half: "pre" | "post"): string {
  return half === "pre"
    ? "Baseline before the ad. Its wording is locked to the post item."
    : "Same question after the ad. The difference is the lift.";
}

export interface LiftTaggedQuestion {
  name?: string;
  x_dt_tags?: Array<string> | null;
  tags?: Array<string> | null;
}

export interface LiftQuestionMapEntry {
  x_dt_tags?: Array<string> | null;
}

export interface LiftPairRef<T> {
  measure: string;
  pre: T;
  post: T;
}

/**
 * Element tags first, then draft `tags`, then the questionMap mirror — same
 * fallback order QuestionCard already uses for a single half.
 */
export function readLiftTagsFromQuestion(
  question: LiftTaggedQuestion | null | undefined,
  questionMap?: Record<string, LiftQuestionMapEntry> | null,
): LiftTagHalf | null {
  if (!question) {
    return null;
  }
  return (
    readQuestionLiftHalf(question.x_dt_tags) ??
    readQuestionLiftHalf(question.tags) ??
    readQuestionLiftHalf(questionMap?.[question.name ?? ""]?.x_dt_tags)
  );
}

export function resolveLiftPair<T extends LiftTaggedQuestion>(
  primary: T,
  sibling: T | null | undefined,
  questionMap?: Record<string, LiftQuestionMapEntry> | null,
): LiftPairRef<T> | null {
  if (!sibling) {
    return null;
  }
  const primaryHalf = readLiftTagsFromQuestion(primary, questionMap);
  const siblingHalf = readLiftTagsFromQuestion(sibling, questionMap);
  if (
    !primaryHalf ||
    !siblingHalf ||
    primaryHalf.measure !== siblingHalf.measure ||
    primaryHalf.half === siblingHalf.half
  ) {
    return null;
  }
  return {
    measure: primaryHalf.measure,
    pre: primaryHalf.half === "pre" ? primary : sibling,
    post: primaryHalf.half === "post" ? primary : sibling,
  };
}

export type CollapsedLiftItem<T> =
  | { kind: "pair"; pair: LiftPairRef<T> }
  | { kind: "single"; question: T };

/**
 * Collapse `lift:pre:<measure>` / `lift:post:<measure>` into one slot.
 * Incomplete halves and untagged questions stay singles so the back catalogue
 * is unchanged. First complete pair per measure wins; later extras stay singles.
 */
export function collapseLiftPairs<T extends LiftTaggedQuestion>(
  questions: Array<T>,
  questionMap?: Record<string, LiftQuestionMapEntry> | null,
): Array<CollapsedLiftItem<T>> {
  const byMeasure = new Map<string, { pre?: T; post?: T }>();
  for (const question of questions) {
    const half = readLiftTagsFromQuestion(question, questionMap);
    if (!half) {
      continue;
    }
    const slot = byMeasure.get(half.measure) ?? {};
    if (half.half === "pre" && !slot.pre) {
      slot.pre = question;
    }
    if (half.half === "post" && !slot.post) {
      slot.post = question;
    }
    byMeasure.set(half.measure, slot);
  }

  const skipped = new Set<T>();
  const pairByFirst = new Map<T, LiftPairRef<T>>();
  for (const [measure, slot] of byMeasure) {
    if (!slot.pre || !slot.post) {
      continue;
    }
    const preIndex = questions.indexOf(slot.pre);
    const postIndex = questions.indexOf(slot.post);
    const first = preIndex <= postIndex ? slot.pre : slot.post;
    const second = first === slot.pre ? slot.post : slot.pre;
    skipped.add(second);
    pairByFirst.set(first, { measure, pre: slot.pre, post: slot.post });
  }

  const items: Array<CollapsedLiftItem<T>> = [];
  for (const question of questions) {
    if (skipped.has(question)) {
      continue;
    }
    const pair = pairByFirst.get(question);
    if (pair) {
      items.push({ kind: "pair", pair });
    } else {
      items.push({ kind: "single", question });
    }
  }
  return items;
}

/**
 * Questions already on a result payload — never invents a sibling. Extra
 * fields (`pairedQuestion`, `questions`) ride through python's verbatim result.
 */
export function collectLiftQuestionsFromResult(result: {
  question?: LiftTaggedQuestion | null;
  pairedQuestion?: LiftTaggedQuestion | null;
  paired_question?: LiftTaggedQuestion | null;
  questions?: Array<LiftTaggedQuestion> | null;
} | null | undefined): Array<LiftTaggedQuestion> {
  if (!result) {
    return [];
  }
  const collected: Array<LiftTaggedQuestion> = [];
  if (result.question) {
    collected.push(result.question);
  }
  if (result.pairedQuestion) {
    collected.push(result.pairedQuestion);
  }
  if (result.paired_question) {
    collected.push(result.paired_question);
  }
  if (Array.isArray(result.questions)) {
    for (const question of result.questions) {
      if (question) {
        collected.push(question);
      }
    }
  }
  return collected;
}

export function findLiftPairOnResult(result: {
  question?: LiftTaggedQuestion | null;
  pairedQuestion?: LiftTaggedQuestion | null;
  paired_question?: LiftTaggedQuestion | null;
  questions?: Array<LiftTaggedQuestion> | null;
} | null | undefined): LiftPairRef<LiftTaggedQuestion> | null {
  const collapsed = collapseLiftPairs(collectLiftQuestionsFromResult(result));
  for (const item of collapsed) {
    if (item.kind === "pair") {
      return item.pair;
    }
  }
  return null;
}

/** Every id one half of a lift pair is known by, most canonical first. */
export interface LiftPairQuestionIds {
  post: Array<string>;
  pre: Array<string>;
}

function stringIds(ids: Array<string | null | undefined>): Array<string> {
  return ids.filter((id): id is string => typeof id === "string" && id.length > 0);
}

/**
 * The ids a SurveyJS element answers to: its canonical question id (from the
 * questionMap mirror, else the element's own `_canonical` block), then its
 * name. Lift rows name the canonical id.
 */
export function liftQuestionIdsOf(
  question: { name?: string; _canonical?: { questionId?: string | null } | null } | null | undefined,
  questionMap?: Record<string, { questionId?: string | null }> | null,
): Array<string> {
  if (!question) {
    return [];
  }
  return stringIds([
    questionMap?.[question.name ?? ""]?.questionId,
    question._canonical?.questionId,
    question.name,
  ]);
}

/** How many rows a compiled SurveyJS grid declares (0 for anything that is not a grid). */
export function compiledGridRowCount(element: unknown): number {
  if (!element || typeof element !== "object") {
    return 0;
  }
  const rows = (element as { rows?: unknown }).rows;
  return Array.isArray(rows) ? rows.length : 0;
}

/**
 * A lift row stored before grid rows were paired one by one: on a grid with
 * two or more rows it carries no `gridRowKey`, so its one figure mixes the
 * rows. Structural only (the grid's declared rows and the row's shape); the
 * row is never shown (same rule as Python
 * `paired_lift_reading.pools_grid_rows`).
 */
export function liftRowPoolsGridRows(row: LiftScorecardRow, postRowCount: number): boolean {
  return row.gridRowKey == null && postRowCount >= 2;
}

/**
 * The card without the rows that pool a grid's rows, each row's grid read
 * from the compiled survey by its question id. `null` when nothing is left.
 * Without a survey nothing is known about the grid and nothing is dropped.
 */
export function withoutPooledGridRows(card: LiftScorecard, survey: unknown): LiftScorecard | null {
  if (!survey) {
    return card;
  }
  const rows = card.rows.filter((row) => {
    const question = row.questionId ? findSurveyQuestion(survey, row.questionId) : null;
    return !liftRowPoolsGridRows(row, compiledGridRowCount(question));
  });
  return rows.length > 0 ? { ...card, rows } : null;
}

export interface LiftScorecardForPairOptions {
  /** Rows the post half's compiled grid declares; drops rows that pool them. */
  postRowCount?: number;
}

/**
 * The Python lift scorecard, narrowed to the rows of ONE pre/post pair.
 *
 * Joined by question id: a row's `questionId` is the post half and its
 * `baselineQuestionId` the pre half. Never by tag text: a lift tag names a
 * grid-level measure token ("lift:post:Brand favourability and
 * consideration"), which is not any row's `measureId`, so a tag join matched
 * nothing on a real grid. A row stored before the ids existed carries none and
 * matches nothing. Lookup only; every figure is Python's.
 */
export function readLiftScorecardForPair(
  source: unknown,
  ids: LiftPairQuestionIds,
  options: LiftScorecardForPairOptions = {},
): LiftScorecard | null {
  if (!source || typeof source !== "object" || ids.post.length === 0) {
    return null;
  }
  const record = source as Record<string, unknown>;
  const card = (record.liftScorecard ?? record.lift_scorecard) as LiftScorecard | null | undefined;
  if (!card || typeof card !== "object" || !Array.isArray(card.rows)) {
    return null;
  }
  const rows = card.rows.filter((row) => {
    if (!row || typeof row.questionId !== "string" || !ids.post.includes(row.questionId)) {
      return false;
    }
    if (liftRowPoolsGridRows(row, options.postRowCount ?? 0)) {
      return false;
    }
    return row.baselineQuestionId == null || ids.pre.includes(row.baselineQuestionId);
  });
  return rows.length > 0 ? { ...card, rows } : null;
}
